<template>
  <AppSection id="user-details" type="page" :title="$t($getConst.pages.pageUserDetails.title)"
              :is-section-loading="isLoading">
    <b-row v-if="user">
      <b-col xl="4">
        <b-card
          :header="$t($getConst.pages.pageUserDetails.personal_details)"
          header-class="text-capitalize"
          header-text-variant="secondary"
          header-bg-variant="transparent"
          header-tag="h4"
          class="shadow-none bg-transparent mb-4"
          no-body
        >
          <b-list-group flush>
            <b-list-group-item class="d-flex">
              <h5 class="text-muted mb-0"><i :class="$getConst.icons.user_person"></i></h5>
              <div class="flex-grow-1 ml-3">
                <h5 class="text-muted text-capitalize">{{ $t($getConst.pages.pageUserDetails.first_name) }}</h5>
                <h5 v-if="!editUserFirstName" class="font-weight-bold text-capitalize">{{firstName}}</h5>
                <b-input v-else class="w-100" v-model="firstName"></b-input>
              </div>
              <div class="flex-grow-0">
                <b-button v-if="!editUserFirstName" variant="sm" @click="editUserFirstName = true">
                  <i :class="$getConst.icons.edit + ' text-secondary '"></i>
                </b-button>
                <b-button v-else variant="sm" @click="updateUserName('firstName')" :disabled="firstName === ''">
                  <i :class="$getConst.icons.check + ' text-success'"></i>
                </b-button>
              </div>
            </b-list-group-item>

            <b-list-group-item class="d-flex">
              <h5 class="text-muted mb-0"><i :class="$getConst.icons.user_person"></i></h5>
              <div class="flex-grow-1 ml-3">
                <h5 class="text-muted text-capitalize">{{ $t($getConst.pages.pageUserDetails.last_name) }}</h5>
                <h5 v-if="!editUserLastName" class="font-weight-bold text-capitalize">{{lastName}}</h5>
                <b-input v-else class="w-100" v-model="lastName"></b-input>
              </div>
              <div class="flex-grow-0">
                <b-button v-if="!editUserLastName" variant="sm" @click="editUserLastName = true">
                  <i :class="$getConst.icons.edit + ' text-secondary '"></i>
                </b-button>
                <b-button v-else variant="sm" @click="updateUserName('lastName')" :disabled="lastName === ''">
                  <i :class="$getConst.icons.check + ' text-success'"></i>
                </b-button>
              </div>
            </b-list-group-item>

            <b-list-group-item class="d-flex">
              <h5 class="text-muted mb-0"><i :class="$getConst.icons.envelope"></i></h5>
              <div class="flex-grow-1 ml-3">
                <h5 class="text-muted text-capitalize">{{ $t($getConst.pages.pageUserDetails.email) }}</h5>
                <h5 class="font-weight-bold">{{user.email}}</h5>
              </div>
            </b-list-group-item>
            <b-list-group-item class="d-flex">
              <h5 class="text-muted mb-0"><i :class="$getConst.icons.phone"></i></h5>
              <div class="flex-grow-1 ml-3">
                <h5 class="text-muted text-capitalize">{{ $t($getConst.pages.pageUserDetails.phone) }}</h5>
                <h5 class="font-weight-bold text-capitalize">{{user.phone}}</h5>
              </div>
            </b-list-group-item>
            <b-list-group-item class="d-flex">
              <h5 class="text-muted mb-0"><i :class="$getConst.icons.comment"></i></h5>
              <div class="flex-grow-1 ml-3">
                <h5 class="text-muted text-capitalize">{{ $t($getConst.pages.pageUserDetails.receive_sms) }}</h5>
                <h5 class="font-weight-bold text-capitalize">{{user.sms ? "yes" : "no"}}</h5>
              </div>
            </b-list-group-item>
          </b-list-group>
        </b-card>
        <b-card
          :header="$t($getConst.pages.pageUserDetails.system_details)"
          header-class="text-capitalize"
          header-text-variant="secondary"
          header-bg-variant="transparent"
          header-tag="h4"
          class="shadow-none bg-transparent"
          no-body
        >
          <b-list-group flush>
            <b-list-group-item class="d-flex">
              <h5 class="text-muted mb-0"><i :class="$getConst.icons.user_plus"></i></h5>
              <div class="flex-grow-1 ml-3">
                <h5 class="text-muted text-capitalize">{{ $t($getConst.pages.pageUserDetails.sign_up) }}</h5>
                <h5 class="font-weight-bold text-capitalize">
                  <b-badge v-if="user.signup_status === -1" pill variant="danger" class="w-auto">{{
                    $t($getConst.pages.pageUserDetails.invitation_expired) }}
                  </b-badge>
                  <b-badge v-else-if="user.signup_status === 0" pill variant="waring" class="w-auto">{{
                    $t($getConst.pages.pageUserDetails.in_progress) }}
                  </b-badge>
                  <b-badge v-else-if="user.signup_status === 1" pill variant="success" class="w-auto">{{
                    $t($getConst.pages.pageUserDetails.completed) }}
                  </b-badge>
                </h5>
              </div>
            </b-list-group-item>
            <b-list-group-item class="d-flex" v-if="user.last_login">
              <h5 class="text-muted mb-0"><i :class="$getConst.icons.sign_in"></i></h5>
              <div class="flex-grow-1 ml-3">
                <h5 class="text-muted text-capitalize">{{ $t($getConst.pages.pageUserDetails.last_login) }}</h5>
                <h5 class="font-weight-bold text-capitalize">{{(new Date(user.last_login)).toUTCString()}}</h5>
              </div>
            </b-list-group-item>
            <b-list-group-item class="d-flex">
              <h5 class="text-muted mb-0"><i :class="$getConst.icons.id_badge"></i></h5>
              <div class="flex-grow-1 ml-3">
                <h5 class="text-muted text-capitalize">{{ $t($getConst.pages.pageUserDetails.portal_id) }}</h5>
                <h5 class="font-weight-bold text-capitalize">{{user.id}}</h5>
              </div>
            </b-list-group-item>
            <b-list-group-item class="d-flex">
              <h5 class="text-muted mb-0"><i :class="$getConst.icons.user_circle_o"></i></h5>
              <div class="flex-grow-1 ml-3">
                <h5 class="text-muted text-capitalize">{{ $t($getConst.pages.pageUserDetails.role) }}</h5>
                <h5 class="font-weight-bold text-capitalize">{{user.role}}</h5>
              </div>
            </b-list-group-item>
            <b-list-group-item class="d-flex">
              <h5 class="text-muted mb-0"><i :class="$getConst.icons.globe"></i></h5>
              <div class="flex-grow-1 ml-3">
                <h5 v-if="user.role==='Vendor'" class="text-muted text-capitalize">{{ $t($getConst.pages.pageUserDetails.scope_and_vendor_id) }}</h5>
                <h5 v-else class="text-muted text-capitalize">{{ $t($getConst.pages.pageUserDetails.scope_and_nav_id) }}</h5>
                <ul class="mb-0 pl-0">
                  <li v-for="(scope, index) in scopes">
                    <p class="d-flex mb-1">
                      {{scope.scope_name}}:
                      <template v-if="editUserNavId !== index">
                        <b class="ml-auto">{{scope[navVendor_id]}}</b>
                        <i v-if="editUserNavId === ''" @click="editUserNavId = index"
                           :class="$getConst.icons.edit + ' text-secondary ml-3 '" role="button"></i>
                      </template>
                      <template v-else>
                        <b-input class="w-100 h-25" v-model="scopes[index][navVendor_id]"></b-input>
                        <b-button variant="sm" :disabled="scope[navVendor_id] === ''" @click="confirmUpdateUserNavId()">
                          <i :class="$getConst.icons.check + ' text-success ml-3'"></i>
                        </b-button>
                      </template>
                    </p>
                  </li>
                </ul>
              </div>
            </b-list-group-item>
          </b-list-group>
        </b-card>
      </b-col>
      <b-col xl="8">
        <div>
          <b-card no-body>
            <b-tabs nav-class="font-weight-bold" card>
              <b-tab :title="$t($getConst.pages.pageUserDetails.last_actions)" title-item-class="text-capitalize "
                     title-link-class="text-secondary text-lg-center" active>
                <b-card-text>

                  <b-alert v-if="user.last_actions.length === 0" show variant="warning">
                    {{ $t($getConst.pages.pageUserDetails.no_actions_found) }}
                  </b-alert>

                  <b-list-group v-else flush>
                    <b-list-group-item v-for="action in user.last_actions"
                                       class="d-flex justify-content-between align-items-center">
                      <div class="w-100 d-flex flex-wrap justify-content-between">
                        <div class="flex-grow-1">
                          <small class="line-height-md text-uppercase text-muted">{{formatDate(action.date)}}</small>
                          <h6 class="font-weight-bold text-capitalize">{{action.name}}</h6>
                        </div>
                        <div class="d-flex justify-content-between align-items-center">
                          <b-badge v-if="action.status.toLowerCase() === 'success'" pill variant="success">
                            {{action.status}}
                          </b-badge>
                          <b-badge v-else-if="action.status.toLowerCase() === 'error'" pill variant="danger">
                            {{action.status}}
                          </b-badge>
                        </div>
                      </div>
                    </b-list-group-item>
                  </b-list-group>

                </b-card-text>
              </b-tab>
              <b-tab :title="$t($getConst.pages.pageUserDetails.last_notifications)" title-item-class="text-capitalize"
                     title-link-class="text-secondary text-lg-center">
                <b-card-text>

                  <b-alert v-if="user.notifications.length === 0" show variant="warning">
                    {{ $t($getConst.pages.pageUserDetails.no_notifications_found) }}
                  </b-alert>

                  <b-list-group v-else flush>
                    <b-list-group-item v-for="notification in user.notifications"
                                       class="d-flex justify-content-between align-items-center">
                      <div class="w-100 d-flex flex-wrap justify-content-between">
                        <div class="flex-grow-1">
                          <small class="line-height-md text-uppercase text-muted">{{formatDate(notification.created_at)}}</small>
                          <h6 class="font-weight-bold text-capitalize">
                            <template v-if="notification.type === 'App\\Notifications\\TimesheetNotification'">
                              Email
                            </template>
                            <template v-else-if="notification.type === 'App\\Notifications\\TimesheetSmsNotification'">
                              Sms
                            </template>
                            <b-link @click="showNotificationData(notification)"  v-b-tooltip.hover
                                :title="$t($getConst.pages.pageUserDetails.show_data)"> <i :class="$getConst.icons.externalLink + ' text-primary'"></i>
                            </b-link>
                          </h6>
                        </div>
                        <div class="d-flex justify-content-between align-items-center">
                          <b-badge v-if="notification.template_type" class="mr-1" pill variant="info">{{
                            notification.template_type }}
                          </b-badge>
                          <b-badge v-if="notification.sent === 1" pill variant="success">
                            Sent
                          </b-badge>
                          <b-badge v-else pill variant="danger">
                            Not sent
                          </b-badge>
                        </div>
                      </div>
                    </b-list-group-item>
                  </b-list-group>

                </b-card-text>
              </b-tab>
            </b-tabs>
          </b-card>
        </div>

      </b-col>
    </b-row>

    <AppConfirmationModal
      id="confirmation-modal"
      :title=$t($getConst.pages.profile.are_you_sure) | capitalize
      ref="confirmUpdateUserNavIdModal"
      @onConfirm="updateUserNavId"
      @onCancel="cancelUpdateUserNavId"
    />

    <ShowNotificationDataModal v-if="notification" :id="notificationDataModal" :notification="notification"></ShowNotificationDataModal>

  </AppSection>
</template>

<script>
  import {getUser, updateUserName, updateUserNavIdInScope} from "../../api/user.api";
  import AppSection from "../../components/common/sections/AppSection";
  import AppConfirmationModal from "../../components/common/modals/AppConfirmationModal";
  import ShowNotificationDataModal from "./ShowNotificationDataModal";

  export default {
    name: "UserDetails",
    components: {ShowNotificationDataModal, AppConfirmationModal, AppSection},
    props: {
      user_id: null
    },
    data() {
      return {
        isLoading: false,
        user: null,
        editUserFirstName: false,
        editUserLastName: false,
        firstName: '',
        lastName: '',
        editUserNavId: '',
        scopes: [],
        notificationDataModal: 'notification-data-modal',
        notification: null,
        navVendor_id:null,
      }
    },
    methods: {
      getUserDetails() {
        this.isLoading = true;
        getUser(this.user_id).then(
          (response) => {

            this.user = response.data;
            this.scopes = JSON.parse(JSON.stringify(response.data.scopes));

            this.firstName = response.data.firstName;
            this.lastName = response.data.lastName;
            this.isLoading = false;
            if(this.user.role==='Vendor'){
              this.navVendor_id="vendor_id";
            }else{
              this.navVendor_id="nav_id"
            }
          },
          (error) => {
            if (error.response.status === 404) {
              this.$snotify.error(this.$getConst.pages.pageUserDetails.redirect_to_users_page, this.$t(this.$getConst.pages.alerts.unknown_error));
            } else {
              this.$snotify.error(this.$getConst.pages.pageUserDetails.redirect_to_users_page, this.$t(this.$getConst.pages.alerts.unknown_error));
            }

            setTimeout(() => {
              this.$router.push({name: this.$getConst.routesNames.administration.users});
            }, 5000);
            this.isLoading = false;
          }
        )
      },
      formatDate(date) {
        date = new Date(date);
        return date.toLocaleString();
      },
      updateUserName(type) {
        this.isLoading = true;

        updateUserName(this.user_id, {firstName: this.firstName, lastName: this.lastName}).then(
          (response) => {
            this.$snotify.success(this.$t(this.$getConst.pages.alerts.success_operation));
            this.isLoading = false;
          },
          (error) => {
            if (type === 'firstName') {
              this.firstName = this.user.firstName;
            }
            if (type === 'lastName') {
              this.lastName = this.user.lastName;
            }
            this.handleApiError(error);
            this.isLoading = false;
          }
        )
        type === 'firstName' ? this.editUserFirstName = false : this.editUserLastName = false;
      },

      confirmUpdateUserNavId() {
        if (this.user.scopes[this.editUserNavId][this.navVendor_id] !== this.scopes[this.editUserNavId][this.navVendor_id]) {
          this.$bvModal.show('confirmation-modal');
        } else {
          this.editUserNavId = '';
        }
      },

      cancelUpdateUserNavId() {
        this.scopes[this.editUserNavId][this.navVendor_id] = this.user.scopes[this.editUserNavId][this.navVendor_id];
        this.editUserNavId = '';
      },

      updateUserNavId() {
        this.isLoading = true;

        let scope = this.scopes[this.editUserNavId];

        updateUserNavIdInScope(this.user_id, scope.scope_id, {[this.navVendor_id]: scope[this.navVendor_id]}).then(
          (response) => {
            this.user.scopes[this.editUserNavId][this.navVendor_id] = scope[this.navVendor_id];
            this.$refs.confirmUpdateUserNavIdModal.close();
            this.$snotify.success(this.$t(this.$getConst.pages.alerts.success_operation));
            this.isLoading = false;
          },
          (error) => {
            this.isLoading = false;
            this.$refs.confirmUpdateUserNavIdModal.close();
            this.handleApiError(error);
          }
        )
      },

      showNotificationData(notification) {
        this.notification = notification;
        this.$nextTick(() => this.$bvModal.show(this.notificationDataModal));
      },
    },
    created() {
      this.getUserDetails();
    }
  }
</script>

<style scoped>
  .list-group-item {
    background-color: transparent;
  }
</style>
